import React, { useEffect, useMemo, useState } from "react"

import moment from "moment-timezone"
import { Link, useNavigate } from "react-router-dom"

import { FeatureFlag } from "@common-shared/config/featureFlags.config"

import { ButtonElement } from "@front-shared/elements/button.element"
import { PageHelmetElement } from "@front-shared/elements/pageHelmet.element"
import { PrimaryText } from "@front-shared/elements/primaryText.element"
import { SpinnerElement } from "@front-shared/elements/spinner.element"
import { ConfirmationHelper } from "@front-shared/helpers/confirmation.helper"

import { MFAType } from "../../config/auth.config"
import { GENERAL_CONFIG } from "../../config/general.config"
import { ROUTES_CONFIG } from "../../config/routes.config"
import { AuthHelper } from "../../helpers/auth.helper"
import { UtilHelper } from "../../helpers/util.helper"
import { useIsFeatureFlagEnabled } from "../../hooks/isFeatureFlagEnabled.hook"
import { AuthService } from "../../services/auth.service"
import { useAppDispatch } from "../../store/appDispatch.hook"
import { useAppSelector } from "../../store/selectors/app.selector"
import { authSelector } from "../../store/selectors/auth.selector"
import { profileSelector } from "../../store/selectors/profile.selector"
import { authResetMFA } from "../../store/thunks/auth.thunks"
import type { IAuthCachedData } from "../../types/data.types"

import editIcon from "@front-shared/assets/images/icons/edit-dark.svg"
import exitIcon from "@front-shared/assets/images/icons/exit-red.svg"

export const AccountDetailsComponent: React.FunctionComponent = () => {
    const dispatch = useAppDispatch()
    const navigate = useNavigate()

    const profile = useAppSelector(profileSelector)
    const authState = useAppSelector(authSelector)

    const isBillerGroupsEnabled = useIsFeatureFlagEnabled(
        FeatureFlag.ONE_VERIFY_BILLER_GROUPS,
    )

    const [authCachedData, setAuthCachedData] = useState<IAuthCachedData>()
    const [mfaPhoneNumber, setMFAPhoneNumber] = useState<string | null>()

    useEffect(() => {
        AuthService.getAuthCache().then(setAuthCachedData)
    }, [])

    // MFA Phone Number is available in the auth cache, but we fetch it separately, skipping cache, for extra safety
    useEffect(() => {
        if (!authCachedData || authCachedData.isFederatedUser) {
            return
        }

        AuthHelper.getMFAPhoneNumber().then(setMFAPhoneNumber)
    }, [authCachedData?.isFederatedUser])

    // Convenience variable
    const totpEnabled = useMemo(() => {
        return authCachedData?.fetchedMFAPreference.preferred === MFAType.TOTP
    }, [authCachedData])

    // Convenience variable
    const smsEnabled = useMemo(() => {
        return (
            authCachedData?.fetchedMFAPreference.preferred === MFAType.SMS &&
            mfaPhoneNumber
        )
    }, [authCachedData, mfaPhoneNumber])

    const onResetMFA = () => {
        if (authCachedData?.isFederatedUser) {
            return
        }

        ConfirmationHelper.enableConfirmation(
            () => {
                if (!totpEnabled && !smsEnabled) {
                    return
                }

                dispatch(
                    authResetMFA({
                        payload: null,
                        navigate,
                    }),
                )
            },
            "Update your MFA Method",
            [
                totpEnabled
                    ? "To change authenticator apps or to switch to SMS-based MFA, you'll need to log out and set up MFA again."
                    : "To change your phone number or to switch to Authenticator App MFA, you'll need to log out and set up MFA again.",
                "After logging back in, you’ll confirm with your current method, then go through MFA setup as if you're a new user.",
            ],
            undefined,
            "Log Out",
            "Never Mind",
            undefined,
            {
                isLeftAligned: true,
                width: 530,
            },
        )
    }

    return (
        <div className="max-w-[840px] mx-auto relative">
            <PageHelmetElement
                title="Account Details"
                defaultPageTitle={GENERAL_CONFIG.defaultPageTitle}
            />

            {!profile || authState.isLoading ? (
                <SpinnerElement containerClassName="mt-[60px] text-center" />
            ) : (
                <div>
                    <PrimaryText size={19} weight="semibold">
                        General
                    </PrimaryText>

                    <div className="mt-[16px]">
                        <div className="flex items-center sm:block">
                            <PrimaryText
                                className="w-[190px] mr-[10px]"
                                typography="textSemibold"
                            >
                                Name
                            </PrimaryText>

                            <PrimaryText
                                typography="text"
                                className="mouseflow-ignore"
                            >
                                {profile.name}
                            </PrimaryText>
                        </div>

                        <div className="flex items-center mt-[8px] sm:block">
                            <PrimaryText
                                className="w-[190px] mr-[10px]"
                                typography="textSemibold"
                            >
                                Email Address
                            </PrimaryText>

                            <PrimaryText
                                typography="text"
                                className="mouseflow-ignore"
                            >
                                {profile.email}
                            </PrimaryText>
                        </div>

                        <div className="flex items-center mt-[8px] sm:block">
                            <PrimaryText
                                className="w-[190px] mr-[10px]"
                                typography="textSemibold"
                            >
                                Password
                            </PrimaryText>

                            <PrimaryText typography="text">
                                ************
                            </PrimaryText>
                        </div>

                        {isBillerGroupsEnabled &&
                        profile.billerGroups?.length ? (
                            <div className="flex items-center mt-[8px] sm:block">
                                <PrimaryText
                                    className="w-[190px] mr-[10px]"
                                    typography="textSemibold"
                                >
                                    Groups
                                </PrimaryText>

                                <PrimaryText typography="text">
                                    {profile.billerGroups
                                        .map(item => item.name)
                                        .join(", ")}
                                </PrimaryText>
                            </div>
                        ) : null}

                        <div className="flex items-center mt-[8px] sm:block">
                            <PrimaryText
                                className="w-[190px] mr-[10px]"
                                typography="textSemibold"
                            >
                                Nirvana Member Since
                            </PrimaryText>

                            <PrimaryText typography="text">
                                {moment(profile.createdAt).format(
                                    GENERAL_CONFIG.defaultMomentDateFormat,
                                )}
                            </PrimaryText>
                        </div>
                    </div>

                    <PrimaryText className="mt-[16px]">
                        To edit, please contact{" "}
                        <a
                            className="text-bold !text-brand-vibrantPurple"
                            href={`mailto:${GENERAL_CONFIG.supportEmail}`}
                        >
                            {GENERAL_CONFIG.supportEmail}
                        </a>
                    </PrimaryText>

                    {authCachedData && !authCachedData.isFederatedUser ? (
                        <>
                            <PrimaryText
                                className="mt-[48px]"
                                size={19}
                                weight="semibold"
                            >
                                Security
                            </PrimaryText>

                            <div className="flex sm:flex-wrap gap-[24px] mt-[16px] max-w-[540px] justify-between">
                                <div>
                                    <PrimaryText
                                        className="whitespace-pre-wrap"
                                        typography="text"
                                        size={20}
                                    >
                                        Multi-Factor Authentication
                                    </PrimaryText>

                                    <PrimaryText>
                                        For enhanced security, we require
                                        enabling at least one method.
                                    </PrimaryText>

                                    {smsEnabled && (
                                        <PrimaryText
                                            className="mt-[12px]"
                                            typography="text"
                                        >
                                            Phone Number:{" "}
                                            {UtilHelper.maskString(
                                                mfaPhoneNumber || "",
                                            )}
                                        </PrimaryText>
                                    )}
                                </div>

                                <ButtonElement
                                    label={
                                        smsEnabled
                                            ? "SMS Enabled"
                                            : totpEnabled
                                              ? "Auth App Enabled"
                                              : "Not Enabled"
                                    }
                                    onClick={onResetMFA}
                                    htmlType="button"
                                    type="default"
                                    size="large"
                                    icon={editIcon}
                                    isRightIcon
                                    className="flex-shrink-0"
                                />
                            </div>
                        </>
                    ) : null}

                    <div className="mt-[48px]" style={{ width: "fit-content" }}>
                        <Link to={ROUTES_CONFIG.logoutUrl}>
                            <ButtonElement
                                label="Log out"
                                type="default"
                                isDanger
                                htmlType="button"
                                size="large"
                                icon={exitIcon}
                            />
                        </Link>
                    </div>
                </div>
            )}
        </div>
    )
}
